import { FIREBASE_PREFIX, MINES_CONTRACT_ADDRESS } from '@/constants/constants'
import { useSession } from '@/context/SessionProvider'
import { Box, Flex, Grid, SimpleGrid, Text } from '@chakra-ui/react'
import { MsgExecuteContractCompat } from '@ninjablaze/sdk-ts'
import { useMutation } from '@tanstack/react-query'
import { FormProvider, useForm, useFormContext } from 'react-hook-form'
import { BigNumberInWei } from '@injectivelabs/utils'
import { useEffect, useRef, useState } from 'react'
import { onValue, query, ref } from 'firebase/database'
import { FirebaseDatabase } from '@/context/FirebaseProvider'
import { GET_USER_MINE_GAME } from '@/api/query'
import { FiatOrCryptoAmount } from '@/components/atoms/Amount/Amount'
import { SOUNDS, useAudio } from '@/context/AudioProvider'
import { StepBadge, StepBadgeColor } from '@/components/atoms/StepBadge/StepBadge'
import { StepsTrack } from '@/components/atoms/StepsTrack/StepsTrack'
import { NumberOfMines } from '../../molecules/NumberOfMines/NumberOfMines'
import { useShallow } from 'zustand/react/shallow'
import { useLazyQuery } from '@apollo/client'
import { currenciesSettings, useMinesStore } from './MinesStore'
import { IFormInput, MinesForm } from './Form'
import { MinesSidebar } from './MinesSidebar'
import { TokenMoney } from '@ninjablaze/sdk-ts'

import { AnimatePresence, motion } from 'framer-motion'
import { RiveFile, useRive, useRiveFile } from '@rive-app/react-canvas-lite'
import { useBalanceStore } from '@/context/UserBalance'
import { useTranslation } from 'next-i18next/pages'

const calculate_multipliers = (rtp: number, mines: number) => {
  let amount = new BigNumberInWei(1)
  let steps = []
  for (let index = 0; index < 25 - mines; index++) {
    amount = amount.multipliedBy(new BigNumberInWei(25 - mines - index).div(25 - index))
    steps.push(new BigNumberInWei(1).dividedBy(amount).multipliedBy(rtp))
  }

  return steps
}

const tranformState = (data: any) => {
  // TODO
  return data
}

export const MinesTemplate = () => {
  const [game, setGame, setInitialized] = useMinesStore(
    useShallow((state) => [state.game, state.setGame, state.setInitialized])
  )
  const setGameState = useMinesStore((state) => state.setGameState)
  const setFormDirty = useMinesStore((state) => state.setFormDirty)

  const firebaseInitialized = useRef(false)

  const { account, isInitialized: isAccountInitialized } = useSession()

  const form = useForm<IFormInput>({
    mode: 'all',
    reValidateMode: 'onChange',
    defaultValues: {
      amount: '',
      mines: '5',
    },
  })

  const denom = useBalanceStore((state) => state.denom)
  useEffect(() => {
    useMinesStore.getState().setDenom(denom)
  }, [denom])

  const [loadLastUserMineGame] = useLazyQuery(GET_USER_MINE_GAME, { fetchPolicy: 'no-cache' })

  useEffect(() => {
    if (!account) {
      if (isAccountInitialized) {
        setInitialized(true)
        setGame(null)
      }
      return
    }

    setInitialized(false)
    loadLastUserMineGame({ variables: { player: account.injectiveAddress } }).then((result) => {
      const game = result.data?.minesGames?.nodes[0] as any
      setGame(game || null)
      setInitialized(true)

      if (game?.bet) {
        useBalanceStore.setState({ denom: game.denom })
        form.setValue('amount', new TokenMoney(game?.bet, game.denom).humanFormat, {
          shouldDirty: true,
          shouldValidate: true,
        })
        form.setValue('mines', result.data?.minesGames?.nodes[0]?.mines.toString() || '3', {
          shouldDirty: true,
          shouldValidate: true,
        })

        form.trigger('amount')
        form.trigger('mines')
      }
    })
  }, [account, isAccountInitialized])

  useEffect(() => {
    if (game && !firebaseInitialized.current) {
      firebaseInitialized.current = true
    }

    if (!game) {
      return
    }

    const sub = onValue(query(ref(FirebaseDatabase, `${FIREBASE_PREFIX}/mines/${game!.id}`)), (snapshot) => {
      const data = tranformState(snapshot.val()) as any
      setGameState(data)
    })

    return () => sub()
  }, [game]) // TODO: Game ID?

  const audio = useAudio()

  useEffect(
    () =>
      audio &&
      useMinesStore.subscribe((state, prevState) => {
        if (prevState && state.gameState) {
          if (state.gameState.status === 'lose' && prevState.gameState?.status !== 'lose') {
            audio.play(SOUNDS.PLAYER_LOST)
          }

          if (state.gameState.cells.length > (prevState.gameState?.cells?.length || 0)) {
            audio.play(SOUNDS.NEW_ROUND)
          }
        }
      }),
    [audio]
  )

  useEffect(() => {
    // TODO: Move form logic to Form Component
    const subscription = form.watch(() => setFormDirty(true))
    return () => subscription.unsubscribe()
  }, [form])

  return (
    <Box mx={'auto'}>
      <Grid
        gap={3}
        gridTemplateColumns={{
          base: '1fr',
          md: '300px auto',
          lg: '360px auto',
          xl: '24% auto 320px',
          '2xl': '360px auto 320px',
        }}
      >
        <FormProvider {...form}>
          <Box order={{ base: 2, md: 1 }}>
            <MinesForm />
          </Box>
          <Box order={{ base: 1, md: 2 }}>
            <MinesPlayground />
          </Box>
          <Box
            gridColumnStart={{ base: 'auto', md: '1', xl: 'auto' }}
            gridColumnEnd={{ base: 'auto', md: 3, xl: 'auto' }}
            order={3}
          >
            <MinesSidebar />
          </Box>
        </FormProvider>
      </Grid>
    </Box>
  )
}

// import BannerBg from '@/public/assets/images/mines/banner-bg.png'
// import BannerGraphic from '@/public/assets/images/mines/banner-graphic.png'
// import { Image as ImageHoc } from '@/components/HOC/Image'

// const MinesBanner = () => {
//   const modal = useAppModal()

//   return (
//     <Flex
//       onClick={() => modal.openModalNew(AppModals.MinesContestModal)}
//       mb={6}
//       overflow={'hidden'}
//       pos={'relative'}
//       py={{ base: 2.5, md: 4 }}
//       alignItems={'center'}
//       justifyContent={'center'}
//       rounded={'xl'}
//       cursor={'pointer'}
//     >
//       <Box maxW={{ base: '200px', lg: '250px', xl: '460px' }} pos={'relative'} zIndex={10}>
//         <Text
//           lineHeight={{ base: 1.1, md: 'auto' }}
//           textAlign={'center'}
//           fontWeight={700}
//           fontSize={{ base: 'md', lg: 'lg' }}
//         >
//           Be the first to get 8 in a row{' '}
//           <Text color={'#5D9FFF'} as={'span'}>
//             and win extra 10 INJ!
//           </Text>
//         </Text>
//         <Text textAlign={'center'} color={'ninja.200'} fontSize={'xs'}>
//           Minimum bet: <b>10 $NBZ</b>, required mode: <b>Multiply</b>
//         </Text>
//       </Box>
//       <ImageHoc pos={'absolute'} w={'full'} h={'full'} inset={0} objectFit={'cover'} src={BannerBg} alt={''} />
//       <ImageHoc
//         left={{ base: -135, sm: -20, md: -20, xl: 0 }}
//         w={{ base: '205px', md: '340px' }}
//         pos={'absolute'}
//         src={BannerGraphic}
//         alt={''}
//       />
//       <ImageHoc
//         right={{ base: -81, sm: -20, md: -20, xl: 0 }}
//         w={{ base: '205px', md: '340px' }}
//         transformOrigin={'center'}
//         pos={'absolute'}
//         transform={{ base: 'rotate(-30deg)', sm: 'rotate(180deg)' }}
//         src={BannerGraphic}
//         alt={''}
//       />
//     </Flex>
//   )
// }

enum Panel {
  MINE = 'MINE',
  GEM = 'GEM',
  UNREVEALED = 'UNREVEALED',
}

const MinesPlayground = () => {
  const { account, sendTransaction, signIn } = useSession()
  const state = useMinesStore((state) => state.gameState)
  const winPanel = useMinesStore((state) => state.winPanel)

  const { addTx, openCell, removeCell, isButtonActionLoading, isFormDirty } = useMinesStore()

  const { play } = useAudio()
  const { mutateAsync: reveal } = useMutation({
    mutationFn: async (cells: Array<number>) => {
      if (!account) {
        signIn()
        return
      }

      const gameId = useMinesStore.getState().game?.id

      if (!gameId) {
        return
      }

      openCell(cells[0])
      play(SOUNDS.PLAYER_BET)

      try {
        await addTx(
          {
            msg: MsgExecuteContractCompat.fromJSON({
              sender: account!.injectiveAddress,
              contractAddress: MINES_CONTRACT_ADDRESS,
              funds: [],
              msg: {
                next_round: {
                  game_id: parseInt(gameId),
                  cells,
                },
              },
            }),
          },
          (msgs: any) => sendTransaction(msgs, { showSuccess: false, gasMultiplier: 0.4 })
        )
      } catch (e) {
        removeCell(cells[0])
        play(SOUNDS.PLAYER_BET_ERROR)
        throw e
      }
    },
  })

  const gameId = useMinesStore((state) => state.game?.id)

  const rtp = useMinesStore((state) => Number(state.game?.rtp || state.contractConfig.rtp))

  const game = useMinesStore((state) => state.game)
  const denom = useMinesStore((state) => state.denom)
  const { watch } = useFormContext<IFormInput>()

  const amount = game?.bet
    ? new TokenMoney(game.bet, game.denom)
    : TokenMoney.fromHuman(watch('amount'), denom || 'inj')

  const numberOfMines = game?.mines || Math.min(24, Number(watch('mines')) || 3).toString()

  const mines = state?.mines || []
  const gems = state?.cells || []

  const remote_user_choice =
    Object.values(state?.rounds || [])
      .map((x) => x.user_choice)
      .join(',')
      .split(',')
      .filter((x) => x !== '')
      .map(Number) || []

  const localCells = useMinesStore((state) => state.localGameState.cells)

  const user_choice = Array.from(new Set([...localCells, ...remote_user_choice]))

  const { riveFile, status } = useRiveFile({
    src: 'assets/images/mines/shuriken.riv',
  })

  const mapColor = (currentStep: number) => {
    // TODO: Extract
    const status = state?.status || ''
    const step = gems.length === 0 ? 0 : gems.length
    if (status === 'lose') {
      if (currentStep >= localCells.length) {
        return undefined
      }

      if (isFormDirty) {
        return undefined
      }

      let minesActive = localCells.filter((cell) => mines.includes(cell)).length

      if (currentStep < localCells.length - minesActive) {
        return StepBadgeColor.GREEN
      }

      if (currentStep >= localCells.length - minesActive) {
        return StepBadgeColor.RED
      }
    }

    if (currentStep < step) {
      return StepBadgeColor.GREEN
    }

    if (currentStep < localCells.length) {
      return StepBadgeColor.ORANGE
    }
  }

  let steps = calculate_multipliers(rtp, parseInt(numberOfMines))
    .slice(0, Number(game?.maxCells || 25 - parseInt(numberOfMines)))
    .map((step, k) => {
      let winAmount = new BigNumberInWei(amount.chainValue).multipliedBy(step)

      const maxWin = TokenMoney.fromHuman(
        currenciesSettings.maxWin[useMinesStore.getState().denom],
        useMinesStore.getState().denom
      )

      if (winAmount.gte(maxWin.chainValue)) {
        return {
          denom: denom,
          color: mapColor(k),
          content: <FiatOrCryptoAmount denom={useMinesStore.getState().denom} iconSize={4} value={maxWin} />,
          winAmount: maxWin.chainValue,
          isMax: true,
        }
      }

      return {
        denom: denom,
        color: mapColor(k),
        content: 'x' + step.toFixed(2, BigNumberInWei.ROUND_DOWN),
        winAmount: winAmount,
        isMax: false,
      }
    })

  let stepWinMaxIndex = steps.findIndex((x) => x.isMax)
  steps = stepWinMaxIndex > 0 ? steps.slice(0, stepWinMaxIndex + 1) : steps

  return (
    <>
      <Box bg={'ninja.600'} h={'full'} flexShrink={0} p={{ base: 4, sm: 5 }} rounded={'2xl'} pos={'relative'}>
        <AnimatePresence>
          {winPanel && (
            <motion.div
              style={{
                position: 'absolute',
                inset: 0,
                background: 'rgba(18, 18, 54, 0.60)',
                zIndex: 20,
              }}
              key={'MinesWinPanel'}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
            >
              <Flex alignItems={'center'} justifyContent={'center'} width={'full'} height={'full'}>
                <Flex
                  rounded={'48px'}
                  boxShadow={'0px -5px 40px 0px #68F276 inset'}
                  px={10}
                  py={14}
                  flexDir={'column'}
                  alignItems={'center'}
                  bg={'rgba(255, 255, 255, 0.01)'}
                  backdropFilter={'blur(12px)'}
                >
                  <Text fontSize={'xl'} fontWeight={700}>
                    Win
                  </Text>
                  <FiatOrCryptoAmount
                    wrapperStyles={{
                      fontSize: '2xl',
                    }}
                    denom={winPanel.payout?.denom || 'inj'}
                    iconSize={6}
                    value={winPanel.payout || new TokenMoney('0', 'inj')}
                  />
                  <Text fontWeight={700}>x{winPanel.multiplier || 0}</Text>
                </Flex>
              </Flex>
            </motion.div>
          )}
        </AnimatePresence>

        <NumberOfMines numberOfMines={parseInt(numberOfMines)} />
        <SimpleGrid mt={4} columns={5} gap={{ base: 1, md: 2 }} mb={{ base: 2, sm: 4 }}>
          {Array.from({ length: 5 }, (_, k1) =>
            Array.from({ length: 5 }, (_, k2) => {
              const index = k2 + k1 * 5
              const isMine = mines.includes(index)
              const isGem = gems.includes(index) || state?.status === 'lose'

              return (
                <Box key={k1 + '-' + k2} bg={'ninja.800'} aspectRatio={'1'} w={'full'} rounded={'xl'}>
                  {status == 'success' && (
                    <MinesPanel
                      riveFile={riveFile!}
                      index={index}
                      isActive={
                        user_choice.includes(index) ||
                        (!!gameId && !isButtonActionLoading && user_choice.length < Number(game?.maxCells || 0))
                      }
                      isOpened={user_choice.includes(index) || gems.includes(index)}
                      nextStep={steps[user_choice.length]}
                      isLoading={user_choice.includes(index)}
                      type={!isMine && !isGem ? Panel.UNREVEALED : isMine ? Panel.MINE : Panel.GEM}
                      reveal={() => reveal([index])}
                    />
                  )}
                </Box>
              )
            })
          )}
        </SimpleGrid>
        <MinesSteps
          steps={steps}
          numberOfMines={numberOfMines}
          isNextStep={user_choice.length < Number(game?.maxCells || 0)}
          step={gems.length === 0 ? 0 : gems.length}
        />
      </Box>
    </>
  )
}

const MinesSteps = ({
  steps,
  step,
  isNextStep,
}: {
  numberOfMines: string
  steps: any[]
  step: number
  isNextStep: boolean
}) => {
  const { t } = useTranslation()
  const game = useMinesStore((state) => state.game)
  const localCells = useMinesStore((state) => state.localGameState.cells)
  const denom = useMinesStore((state) => state.denom)
  const value = new TokenMoney(steps[localCells.length]?.winAmount.toNumber(), denom)

  return (
    <Flex gap={{ base: 1, sm: 3 }}>
      {game && isNextStep && (
        <Box flexShrink={0}>
          <StepBadge
            content={
              <Flex gap={2} alignItems={'center'} pos={'relative'}>
                <Text
                  top={-2.5}
                  fontWeight={700}
                  fontSize={{ base: '8px', sm: 'xs' }}
                  pos={{ base: 'absolute', sm: 'static' }}
                >
                  {t('mines.next-step')}
                </Text>
                <Box pos={'relative'} top={0.5}>
                  <FiatOrCryptoAmount iconSize={4} value={value} denom={denom} />
                </Box>
              </Flex>
            }
          />
        </Box>
      )}
      <StepsTrack step={step}>
        {steps.map((x, index) => {
          return <StepBadge key={index} color={x.color} content={x.content} />
        })}
      </StepsTrack>
    </Flex>
  )
}

const MinesPanel = ({
  riveFile,
  nextStep,
  reveal,
  type,
  isActive,
  index,
  isLoading,
  isOpened,
}: {
  riveFile: RiveFile
  nextStep: any
  reveal: () => void
  type: Panel
  isActive: boolean
  index: number
  isLoading: boolean
  isOpened: boolean
}) => {
  const isUnrevealed = type === Panel.UNREVEALED && !isLoading

  const { rive, RiveComponent } = useRive({
    riveFile: riveFile,
    stateMachines: 'State Machine 1',
    autoplay: true,
  })

  const [isHovered, setHovered] = useState(false)

  useEffect(() => {
    const inputs = rive?.stateMachineInputs('State Machine 1')
    if (inputs) {
      inputs.find((input) => input.name === ((index % 4) + 1).toString())!.value = isLoading

      inputs.find((input) => input.name === 'opacity')!.value = 100

      inputs.find((input) => input.name === 'mine instant')!.value = type === Panel.MINE
      inputs.find((input) => input.name === 'gem instant')!.value = type === Panel.GEM

      inputs.find((input) => input.name === 'mine')!.value = type === Panel.MINE && isOpened
      inputs.find((input) => input.name === 'gem')!.value = type === Panel.GEM && isOpened

      inputs.find((input) => input.name === 'hover')!.value = isActive && isHovered

      rive?.startRendering()
    }
  }, [rive, riveFile, isHovered, isOpened, isLoading, type])

  useEffect(() => {
    if (type === Panel.GEM || type === Panel.MINE) {
      setHovered(false)
    }
  }, [type])

  const { play } = useAudio()

  const changeOpacity = !isActive || (type != Panel.UNREVEALED && !isLoading)
  const boxShadowOpacity = changeOpacity ? 0.6 : 1

  const nextStepFormat = nextStep && new TokenMoney(nextStep.winAmount, nextStep.denom)

  return (
    <Flex
      onClick={() => {
        if (isUnrevealed && isActive) {
          reveal()
        }
      }}
      onMouseOver={() => {
        setHovered(true)
        if (isUnrevealed && isActive) {
          play(SOUNDS.HAND_CHANGE)
        }
      }}
      onMouseLeave={() => {
        setHovered(false)
      }}
      w={'full'}
      h={'full'}
      rounded={'xl'}
      cursor={isUnrevealed && isActive ? 'pointer' : 'auto'}
      transition={'0.3s'}
      justifyContent={'center'}
      alignItems={'center'}
      opacity={changeOpacity ? 0.4 : 1}
      boxShadow={
        type === Panel.UNREVEALED
          ? 'none'
          : `0px -5px 26px 0px ${
              type === Panel.GEM ? `rgba(74, 240, 93, ${boxShadowOpacity})` : `rgba(255, 27, 27, ${boxShadowOpacity})`
            } inset;`
      }
      pos={'relative'}
    >
      {nextStep && isHovered && !isLoading && !isOpened && isUnrevealed && isActive && (
        <Flex
          display={{ base: 'none', xl: 'flex' }}
          justifyContent={'center'}
          pos={'absolute'}
          left={0}
          right={0}
          top={'50%'}
          transform={'translateY(-50%)'}
        >
          <FiatOrCryptoAmount
            denom={nextStepFormat?.denom}
            wrapperStyles={{
              fontSize: 'sm',
            }}
            currencyStyles={{
              display: 'none',
            }}
            value={nextStepFormat}
          />
        </Flex>
      )}
      <RiveComponent />
    </Flex>
  )
}
